/**
 * @file 学びメモに書き足した文 (api/noteEntries.ts) を期間で読む hook である。
 *
 * @what: まなぶ人と期間 [from, to] が決まったら1回読み、日付の昇順の配列を返す。読み込み中と失敗は
 *   区別して返す。
 * @failure: 取得失敗は `error` (呼び手は1行で知らせる)。まなぶ人が未定・enabled でないときは `idle`。
 */

import { useEffect, useState } from "react";
import { fetchNoteEntries, type StudyNoteEntry } from "../api/noteEntries";

export type NoteEntriesState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "ready"; entries: StudyNoteEntry[] }
  | { status: "error" };

export function useNoteEntries(
  familyId: string,
  personId: string | null,
  from: string,
  to: string,
  enabled = true,
): NoteEntriesState {
  const [state, setState] = useState<NoteEntriesState>({ status: "idle" });

  useEffect(() => {
    if (!enabled || !personId) {
      setState({ status: "idle" });
      return;
    }
    const controller = new AbortController();
    setState({ status: "loading" });
    fetchNoteEntries(familyId, personId, from, to, controller.signal)
      .then((entries) => {
        if (!controller.signal.aborted) setState({ status: "ready", entries });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        console.error("[study] failed to load note entries", err);
        setState({ status: "error" });
      });
    return () => controller.abort();
  }, [familyId, personId, from, to, enabled]);

  return state;
}
